/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import BaseApi, {EventData} from '../api/BaseApi';
import {COMPONENT, ComponentApi as Contract, OpenDataParam} from '@adobe/exc-app/component';
import {Events} from '@exc/shared';
import type {RuntimeConfiguration} from '@adobe/exc-app';

type PromiseReject = (reason?: any) => void;
type PromiseResolve = (value?: any | PromiseLike<any>) => void;

export class ComponentApi extends BaseApi implements Contract {
  protected apiEvents = ['component'];
  protected defaultType = 'component';
  protected keyPrefix = 'component';

  protected config: RuntimeConfiguration | null = null;
  protected promiseReject: PromiseReject | null = null;
  protected promiseResolve: PromiseResolve | null = null;

  public close(data?: any): void {
    this.messenger.send(Events.COMPONENT_CLOSE, data);
  }

  public isComponent(): boolean {
    return !!this.config?.inModal;
  }

  public onConfigure(config: RuntimeConfiguration): void {
    this.config = config;
  }

  public open(component: COMPONENT, data?: OpenDataParam): Promise<Record<string, any>> {
    return new Promise((res: PromiseResolve, rej: PromiseReject) => {
      if (!component || Object.values(COMPONENT).indexOf(component) === -1) {
        const message = `${component} is an invalid component type, valid types are: ${Object.values(COMPONENT).join(', ')}`;
        this.metrics.warn(message);
        return rej(message);
      }
      if (this.isComponent()) {
        const message = `${this.config?.appId} is already loaded as a component, can only have one at a time.`;
        this.metrics.warn(message);
        return rej(message);
      }
      if (this.promiseResolve) {
        const message = 'This application has already initiated a component modal, only one can exist at a time.';
        this.metrics.warn(message);
        return rej(message);
      }
      this.promiseReject = rej;
      this.promiseResolve = res;
      this.messenger.send(Events.COMPONENT_OPEN, {component, data});
    });
  }

  public receive(event: MessageEvent): void {
    const {type, value}: EventData = event.data || {};

    if (type === Events.COMPONENT_CLOSE_COMPLETE && this.promiseResolve) {
      this.promiseResolve(value);
      this.promiseReject = null;
      this.promiseResolve = null;
    }
  }
}
